// frontend_app/src/App.js
import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import './App.css';

import { PreferencesProvider } from './context/PreferencesContext';
import { ToastProvider } from './context/ToastContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SourcesProvider } from './context/SourcesContext';
import { AppShell } from './components/layout/AppShell';
import { BrandMark } from './components/layout/Brand';
import { Spinner } from './components/ui/misc';
import LoginPage from './pages/LoginPage';
import SearchPage from './pages/SearchPage';
import ResourcesPage from './pages/ResourcesPage';
import { ForbiddenPage, NotFoundPage } from './pages/StatusPages';

// Admin screens are only downloaded by administrators.
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminSourcesPage = lazy(() => import('./components/admin/AdminSourcesPage'));
const AdminUsersPage = lazy(() => import('./components/admin/AdminUsersPage'));

const FullScreenLoader = ({ label = 'Loading…' }) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-canvas text-sm text-muted">
    <BrandMark size="lg" />
    <Spinner label={label} />
  </div>
);

const RequireAdmin = ({ children }) => {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <AppShell><ForbiddenPage /></AppShell>;
  return <Suspense fallback={<FullScreenLoader label="Loading admin…" />}>{children}</Suspense>;
};

const SignedInApp = () => (
  <SourcesProvider>
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<SearchPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
        <Route index element={<Navigate to="sources" replace />} />
        <Route path="sources" element={<AdminSourcesPage />} />
        <Route path="users" element={<AdminUsersPage />} />
      </Route>
    </Routes>
  </SourcesProvider>
);

/** Shows sign-in until there is a session; the requested URL is kept. */
const AuthGate = () => {
  const { status } = useAuth();
  if (status === 'loading') return <FullScreenLoader />;
  if (status === 'anonymous') return <LoginPage />;
  return <SignedInApp />;
};

function App() {
  return (
    <PreferencesProvider>
      <ToastProvider>
        <AuthProvider>
          <AuthGate />
        </AuthProvider>
      </ToastProvider>
    </PreferencesProvider>
  );
}

export default App;
